/**
 * Data-source hook for the `/settings/access` (Kanjoy Users) page — task
 * 104d.1 (mockup wiring) + 104d.2 (live wiring).
 *
 * Returns the org's Memberships (active + deactivated) and Pending invites
 * in a single shape the page can render. Split from the Employees surface
 * because the two entities are distinct: Employees = billable roster,
 * Memberships = Kanjoy users with org access.
 *
 * Mockup-mode branch reads the fictional KANJOY_USERS + PENDING_INVITES
 * fixtures. Live-mode branch (104d.2) calls:
 *   GET /api/v1/orgs/:orgId/members?includeDeactivated=true
 *   GET /api/v1/orgs/:orgId/invites
 * and normalises the responses into the same KanjoyUser / PendingInvite
 * shapes the page already consumes in mockup mode. Keeping the two branches
 * shape-compatible means the page component does not branch on mockupMode.
 *
 * The invite modal's email-lookup uses a separate narrow hook
 * (`useEmployeeSearch`) — do not add employee search here.
 *
 * Mutation hooks (invite / resend / revoke invite, change role, revoke /
 * reactivate membership) are colocated at the bottom of this file. They
 * invalidate both the `members` and `invites` queries on success so the
 * page refetches in place without a full reload.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { KANJOY_USERS, type KanjoyUser } from '@/data/kanjoyUsers';
import { PENDING_INVITES, type PendingInvite } from '@/data/pendingInvites';

export type { KanjoyUser, KanjoyUserRole, VisibilityScope } from '@/data/kanjoyUsers';
export type { PendingInvite } from '@/data/pendingInvites';

export interface UseOrgAccessResult {
  users: readonly KanjoyUser[];
  pendingInvites: readonly PendingInvite[];
  isLoading: boolean;
}

// Shape of a member row as returned by the backend (members.ts 104d.2).
interface ApiMember {
  id: string;
  userId: string;
  email: string | null;
  name: string | null;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
  deactivatedAt: string | null;
  employeeId: string | null;
  visibilityScope: 'self' | 'all' | 'own_bu' | 'bu_list' | 'employee_list';
}

// Shape of an invite row as returned by the backend (invites.ts 104b).
interface ApiInvite {
  id: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  employeeId: string | null;
  invitedById: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

// 104f D5: the full 5-value enum flows through to the UI — no collapsing.
function toKanjoyUser(row: ApiMember): KanjoyUser {
  return {
    id: row.id,
    userId: row.userId,
    email: row.email ?? '',
    name: row.name ?? row.email ?? 'Unnamed user',
    role: row.role,
    createdAt: row.createdAt,
    deactivatedAt: row.deactivatedAt,
    employeeId: row.employeeId,
    visibilityScope: row.visibilityScope,
  };
}

function toPendingInvite(row: ApiInvite, inviterNames: Map<string, string>): PendingInvite {
  return {
    id: row.id,
    email: row.email,
    role: row.role,
    employeeId: row.employeeId,
    expiresAt: row.expiresAt,
    createdAt: row.createdAt,
    createdBy:
      (row.invitedById ? inviterNames.get(row.invitedById) : undefined) ??
      // When the inviter's display name isn't resolvable from the member
      // list (system-created invites, or an inviter who has since been
      // revoked), fall back to "Kanjoy" — the same default createInvite
      // uses in outbound invite email copy.
      'Kanjoy',
  };
}

const MEMBERS_QUERY_KEY = 'accessMembers';
const INVITES_QUERY_KEY = 'accessInvites';

export function useOrgAccess(): UseOrgAccessResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const membersQuery = useQuery({
    queryKey: [MEMBERS_QUERY_KEY, orgId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{ members: ApiMember[] }>(
        `/api/v1/orgs/${orgId}/members?includeDeactivated=true`,
        token,
      );
      return res.members.map(toKanjoyUser);
    },
    enabled: !mockupMode && !!orgId,
  });

  const invitesQuery = useQuery({
    queryKey: [INVITES_QUERY_KEY, orgId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<{ invites: ApiInvite[] }>(`/api/v1/orgs/${orgId}/invites`, token);
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) {
    return {
      users: KANJOY_USERS,
      pendingInvites: PENDING_INVITES,
      isLoading: false,
    };
  }

  const users = membersQuery.data ?? [];
  // Build a userId → display name map so we can resolve `invitedById` into
  // a readable "Invited by" column without requiring a backend join.
  const inviterNames = new Map<string, string>();
  for (const u of users) inviterNames.set(u.userId, u.name);

  const pendingInvites = (invitesQuery.data?.invites ?? []).map((i) =>
    toPendingInvite(i, inviterNames),
  );

  return {
    users,
    pendingInvites,
    isLoading: membersQuery.isLoading || invitesQuery.isLoading,
  };
}

// ── Mutation hooks ──────────────────────────────────────────────────────
// Each mutation invalidates both the members and invites queries so the
// page refetches without a manual refresh. Mockup-mode branches
// short-circuit to a resolved promise — the fixtures are read-only, so the
// list does not update, mirroring the 104c convention.

function useAccessInvalidator() {
  const { currentOrg } = useAppContext();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [MEMBERS_QUERY_KEY, currentOrg.id] });
    void qc.invalidateQueries({ queryKey: [INVITES_QUERY_KEY, currentOrg.id] });
  };
}

export interface CreateInviteBody {
  email: string;
  role: 'owner' | 'admin' | 'member';
  employeeId?: string;
}

export function useInvite() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async (body: CreateInviteBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<{ invite: ApiInvite }>(`/api/v1/orgs/${currentOrg.id}/invites`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: invalidate,
  });
}

export function useResendInvite() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${currentOrg.id}/invites/${inviteId}/resend`, token, {
        method: 'POST',
      });
    },
    onSuccess: invalidate,
  });
}

export function useRevokeInvite() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${currentOrg.id}/invites/${inviteId}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: invalidate,
  });
}

export interface ChangeRoleBody {
  membershipId: string;
  role: 'owner' | 'admin' | 'member';
}

export function useChangeRole() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async ({ membershipId, role }: ChangeRoleBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${currentOrg.id}/memberships/${membershipId}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
    },
    onSuccess: invalidate,
  });
}

export function useRevokeMembership() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async (membershipId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(
        `/api/v1/orgs/${currentOrg.id}/memberships/${membershipId}/revoke`,
        token,
        { method: 'POST' },
      );
    },
    onSuccess: invalidate,
  });
}

// 104f D5: visibility editor — GET returns `{ scope, businessUnitIds,
// employeeIds }`; PUT replaces the row transactionally on the backend.
export interface UpdateVisibilityBody {
  membershipId: string;
  scope: 'self' | 'all' | 'own_bu' | 'bu_list' | 'employee_list';
  businessUnitIds?: string[];
  employeeIds?: string[];
}

export function useUpdateVisibility() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async (body: UpdateVisibilityBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(
        `/api/v1/orgs/${currentOrg.id}/memberships/${body.membershipId}/visibility`,
        token,
        {
          method: 'PUT',
          body: JSON.stringify({
            scope: body.scope,
            businessUnitIds: body.businessUnitIds,
            employeeIds: body.employeeIds,
          }),
        },
      );
    },
    onSuccess: invalidate,
  });
}

export function useReactivateMembership() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useAccessInvalidator();

  return useMutation({
    mutationFn: async (membershipId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(
        `/api/v1/orgs/${currentOrg.id}/memberships/${membershipId}/reactivate`,
        token,
        { method: 'POST' },
      );
    },
    onSuccess: invalidate,
  });
}
